import type { GigsafeConfig, SaveGigsafeConfigPayload } from 'src/interfaces/gigsafeConfig'
import { blankToNull, isEmptyRichText } from 'src/utils/gigsafeConfig'
import type { CustomerSettingsFormValues } from './CustomerSettingsForm.interface'

const toText = (value: string | null): string => {
  return value ?? ''
}

const toRichTextPayload = (value: string): string | null => {
  if (isEmptyRichText(value)) {
    return null
  }

  return value
}

export function toFormValues(config: GigsafeConfig | null): CustomerSettingsFormValues {
  if (config === null) {
    return {
      onboardingSuccessScreenTitle: '',
      onboardingSuccessScreenDescription: '',
      onboardingDelayedStepTitle: '',
      onboardingDelayedStepDescription: '',
      onboardingSuccessScreenDelayedTitle: '',
      onboardingSuccessScreenDelayedDescription: '',
      loanTransferEnabled: false,
      wireTransferEnabled: false,
      masterContractorDocusignTemplateId: '',
      internalDocumentReviewInstructions: '',
    }
  }

  return {
    onboardingSuccessScreenTitle: toText(config.onboardingSuccessScreenTitle),
    onboardingSuccessScreenDescription: toText(config.onboardingSuccessScreenDescription),
    onboardingDelayedStepTitle: toText(config.onboardingDelayedStepTitle),
    onboardingDelayedStepDescription: toText(config.onboardingDelayedStepDescription),
    onboardingSuccessScreenDelayedTitle: toText(config.onboardingSuccessScreenDelayedTitle),
    onboardingSuccessScreenDelayedDescription: toText(
      config.onboardingSuccessScreenDelayedDescription,
    ),
    loanTransferEnabled: Boolean(config.loanTransferEnabled),
    wireTransferEnabled: Boolean(config.wireTransferEnabled),
    masterContractorDocusignTemplateId: toText(config.masterContractorDocusignTemplateId),
    internalDocumentReviewInstructions: toText(config.internalDocumentReviewInstructions),
  }
}

/**
 * Every field is sent explicitly, and cleared fields are sent as `null` rather than omitted:
 * the backend's update runs `_.omitBy(data, _.isUndefined)`, so an omitted field would leave
 * the old value in the database while the UI showed the field as blank.
 */
export function toSavePayload(
  partnerId: string,
  values: CustomerSettingsFormValues,
): SaveGigsafeConfigPayload {
  return {
    partnerId,
    onboardingSuccessScreenTitle: blankToNull(values.onboardingSuccessScreenTitle),
    onboardingSuccessScreenDescription: toRichTextPayload(
      values.onboardingSuccessScreenDescription,
    ),
    onboardingDelayedStepTitle: blankToNull(values.onboardingDelayedStepTitle),
    onboardingDelayedStepDescription: toRichTextPayload(values.onboardingDelayedStepDescription),
    onboardingSuccessScreenDelayedTitle: blankToNull(values.onboardingSuccessScreenDelayedTitle),
    onboardingSuccessScreenDelayedDescription: toRichTextPayload(
      values.onboardingSuccessScreenDelayedDescription,
    ),
    loanTransferEnabled: values.loanTransferEnabled,
    wireTransferEnabled: values.wireTransferEnabled,
    // `@IsUUID()` rejects an empty string, which would fail the entire save.
    masterContractorDocusignTemplateId: blankToNull(values.masterContractorDocusignTemplateId),
    internalDocumentReviewInstructions: toRichTextPayload(
      values.internalDocumentReviewInstructions,
    ),
  }
}
